import { Directive, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PgFormField, PgFormLayout } from '../../models/form.model';
import { DataService } from '../../services/data.service';
import { LocalizationService } from '../../services/localization.service';
import { NotificationsService } from '../../services/notifications.service';
import { AuthService } from '../../services/auth.service';
import { SaveStatusRequest, SaveStatusService } from '../../pg-ui-molecules/save-status.service';
import { EnvironmentService } from '../../services/environment.service';
import { OptionMapsService } from '../../services/option-maps.service';
import { PgFormComponent } from '../../pg-form/pg-form/pg-form.component';
import { PGUtilities } from '../../utilities/pg-utilities';

@Directive()
export abstract class SingleFormComponent implements OnChanges, OnDestroy { // TODO: ereditare da SingleFormMain, portare gestione traduzioni in classe padre

    @Input() elementId:string = null;
    @Input() returnUrl:string = null;
    @Input() returnFragment:string = null;

    @Input() viewOnly:boolean;

    @Output() valuesChange = new EventEmitter<any>();

    constructor(protected dataService:DataService, protected authService:AuthService, protected localizationService:LocalizationService, protected router:Router, protected route:ActivatedRoute, protected notificationsService:NotificationsService, protected saveStatusService:SaveStatusService, protected optionMapsService:OptionMapsService, protected environmentService:EnvironmentService) { }

    abstract resourceId:string;

    abstract formLayout:PgFormLayout;

    protected async beforeInitializeForm() {}
    protected async afterInitializeForm() {}
    protected async afterLoadData(values:any) { return values; }
    protected beforeValueChanges(values:any, changes:any) { return values; }

    protected adaptLoadData(values:any) { return values; }
    protected adaptSaveData(values:any) {
        if(values != null && typeof values.languages != 'undefined') {
            values.languages = PGUtilities.tryParseJSON(values.languages)
        }

        return values;
    }

    protected async beforeSaveData(values:any) { return values; }

    selectOptions:{ [select:string] : Array<{ value:string, text:string }> } = {}

    private _hasTranslations = false;

    private async _checkInitializeForm() {
        if(!this._isFormInitialized) {
            this._isFormInitialized = true;

            await this.beforeInitializeForm()

            // AUTOMATISMI: label, options, layout checkbox, switch

            this.selectOptions = this.optionMapsService.getResourceOptionMaps(this.resourceId);

            let _setFileDir:Array<PgFormField> = []

            for(let _field of this.formLayout.fieldList) {
                if(/@.*Translation$/.test(_field.name)) {
                    this._hasTranslations = true;
                }

                let _cleanFieldName = _field.name.replace(/@.*$/, '')

                for(let _prop of ['label','tooltip','placeholder']) {
                    if(_field[_prop] == 'auto') {
                        let _translated = this.localizationService.translate('RESOURCES.' + this.resourceId + '.fields.' + _cleanFieldName + '.' + _prop, null, null, true);

                        if(_translated) {
                            _field[_prop] = _translated;
                        }
                        else {
                            _field[_prop] = null;
                        }
                    }
                }

                if(_field.type == 'select') {
                    if(_field.options == null) {
                        _field.options = this.selectOptions[_field.name]
                    }

                    if(/accessibility/.test(_field.name)) {
                        this.formLayout.getFieldLayout(_field.name).display = { oneLine: true }
                    }
                    else if(_field.name == 'language' || _field.name == 'languages') {
                        this.formLayout.getFieldLayout(_field.name).display = { multiLine: true }
                    }
                }
                else if(_field.type == 'html' || _field.type == 'file' || _field.type == 'location' || _field.type == 'embed' || _field.type == 'info') {
                    this.formLayout.getFieldLayout(_field.name).display = { fullWidth: true }
                }

                if(_field.name == 'realm_id') {
                    _field.default = this.authService.user.realm?.id;
                    _field.visible = this.authService.user.getSelectableRealms().length > 1;
                }
                else if(_field.name == 'language' || _field.name == 'languages') {
                    if(_field.multi) {
                        _field.default = JSON.stringify(this.localizationService.defaultContentLanguages);
                    }
                    else {
                        _field.default = this.localizationService.defaultContentLanguages[0]
                    }
                }

                if(_field.type == 'file' && _field.fileDirectory == null) {
                    if(this.resourceId != 'File') {
                        _setFileDir.push(_field)
                    }
                }
            }

            if(_setFileDir.length > 0) {
                let _resourceDir = await new Promise<string>((resolve, reject) => {
                    this.dataService.resolveDirectoryPath(['default', this.resourceId]).subscribe((data) => {
                        resolve(data)
                    })
               }) 

                for(let _field of _setFileDir) {
                    _field.fileDirectory = _resourceDir;
                }
            }

            await this.afterInitializeForm()

            if(this.environmentService.environment.CustomFormConfig != null) {
                for(let _id of ['*', this.resourceId]) {
                    let _config = this.environmentService.environment.CustomFormConfig[_id];

                    if(_config != null) {
                        for(let i in _config) {
                            let _field = this.formLayout.getFieldByName(i);
    
                            if(_field != null) {
                                for(let k in _config[i]) {
                                    _field[k] = _config[i][k];
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    private _isFormInitialized = false;

    isLoading = false;

    openMode:'create'|'edit' = null;

    private _translationByLanguage:{
        [language:string]: any
    } = null;

    private _getTranslationsIdField() {
        return this.resourceId.replace(/([A-Z])/g, '_$1').replace(/^_/g, '').toLowerCase() + '_id'
    }

    ngOnChanges(): void {
        this.isLoading = true;

        this._translationByLanguage = {};

        let _translationIdField = this._getTranslationsIdField();

        for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
            let _cLanguageBaseObj:any = {
                language: _cLanguage
            }

            _cLanguageBaseObj[_translationIdField] = this.elementId;

            this._translationByLanguage[_cLanguage] = _cLanguageBaseObj;
        }

        this.openMode = this.dataService.isDraftId(this.elementId) ? 'create' : 'edit';

        this._checkRemoveSaveRequest();

        this._checkInitializeForm().then(() => {
            this.formLayout.resetData();

            this.loadData().then((data) => {
                this.isLoading = false;

                this.formLayout.setData(data)
    
                this._checkCreateSaveRequest()
            })
        })
    }

    protected _saveRequest:SaveStatusRequest = null;

    onAdvancedModeChange(val:boolean) {
        if(this._saveRequest) {
            this._saveRequest.toggleValues.advanced = val;
        }
    }

    protected _checkCreateSaveRequest() {
        this.saveStatusService.clearTopic('app-single-form-main')
        this._saveRequest = null;

        if(!this.viewOnly) { 
            // TODO: nella create dovrebbe andarci anche la reset per cancellare il draft

            let _resourceTranslation = this.resourceId.replace(/([A-Z])/g, '-$1').toLowerCase()

            // TODO: gestire lingue
            this._saveRequest = this.saveStatusService.createRequest(this.openMode, 'forms-' + _resourceTranslation + '.' + _resourceTranslation + '-' + this.openMode, (action) => {
                if(action == 'cancel') {
                    this.cancelEdit()
                }
                else if(action == 'save') {
                    this.saveData()
                }
                else if(action == 'delete') {
                    this.deleteDraft()
                }
            }, { save: true, cancel: true, delete: this.openMode == 'create' ? true : null }, 
            (toggle, value) => {
                if(toggle == 'advanced') {
                    this.formLayout.advancedMode = value;
                    if(this.formComponent != null) {
                        this.formComponent.setAdvancedMode(value)
                    }
                }
            }, { advanced: this.formLayout?.hasAdvancedMode() ? true : null },
            (language) => {
                this.setSelectedLanguage(language)
            }, 
            null, (languages) => {
                this.goToLanguagesManagement()
            }, 'app-single-form-main')

            this._saveRequest.getLanguagesFromForm(this.formLayout, this.localizationService.availableApplicationLanguages) 
        }
    }

    selectedLanguage:string = null;

    setSelectedLanguage(language:string) {
        this.selectedLanguage = language;
    }

    @ViewChild('formComponent') formComponent:PgFormComponent;

    goToLanguagesManagement() {
        let _field = this.formLayout.getFieldByName('languages');

        if(_field != null) {
            this.formComponent.scrollToField(_field, 'Primary')
        }
    }

    protected _checkRemoveSaveRequest() {
        if(this._saveRequest != null) this.saveStatusService.removeRequest(this._saveRequest)
    }

    ngOnDestroy() {
        this._checkRemoveSaveRequest();
    }

    private _mapTranslationsToData(data:any, translations:Array<any>) {
        if(translations != null) {
            for(let _item of translations) {
                this._translationByLanguage[_item.language] = _item;
    
                for(let i in _item) {
                    if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at' && i != 'language') {
                        let _localeValue:any = {};
    
                        for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
                            if(this._translationByLanguage[_cLanguage]) {
                                _localeValue[_cLanguage] = this._translationByLanguage[_cLanguage][i];
                            }
                            else {
                                _localeValue[_cLanguage] = {}
                            }
                        }
    
                        data[i + '@' + this.resourceId + 'Translation'] = JSON.stringify(_localeValue);
                    }
                }
            }
        }
    }

    protected loadData() {
        return new Promise<any>((resolve, reject) => {
            if(this.dataService.isDraftId(this.elementId)) {
                this.dataService.getDraftElementData(this.resourceId, this.elementId).subscribe((data) => {
                    if(data.translations) {
                        let _translationData = data.translations
                        delete data.translations
                        this._mapTranslationsToData(data, _translationData)
                    }

                    data = this.adaptLoadData(data);

                    this.afterLoadData(data).then((data) => {
                        resolve(data)
                    })
                })
            }
            else {
                this.dataService.getElementData(this.resourceId, this.elementId).subscribe((data) => {
                    this._checkLoadTranslations(data).then((translations) => {
                        this._mapTranslationsToData(data, translations)

                        data = this.adaptLoadData(data);

                        this.afterLoadData(data).then((data) => {
                            resolve(data)
                        })
                    })
                })
            }
        })
    }

    private _checkLoadTranslations(data:any) {
        return new Promise<any>((resolve, reject) => {
            if(data.translations) {
                let _translationData = data.translations

                delete data.translations

                resolve(_translationData)
            }
            else {
                if(!this._hasTranslations) {
                    resolve(null)
                }
                else {
                    this.dataService.getResourceData(this.resourceId + 'Translation', null, this.resourceId, this.elementId).subscribe((translationData) => {        
                        resolve(translationData)
                    })
                }
            }
        })
    }

    onValueChange(changes:any) {
        let _values = this.formLayout.getData();
        _values = this.beforeValueChanges(_values, changes);
        _values = this.adaptSaveData(_values);

        if(this.dataService.isDraftId(this.elementId)) {
            this.dataService.saveDraftElementData(this.resourceId, this.elementId, _values).subscribe(() => {})
        }

        this._saveRequest.getLanguagesFromForm(this.formLayout, this.localizationService.availableApplicationLanguages) 

        this.valuesChange.emit(_values)
    }

    private _updateTranslations(values:any) {
        let _keysToRemove = [];

        for(let i in values) {
            let _cResource = i.split('@')[1];

            if(_cResource != null) {
                _keysToRemove.push(i);

                if(values[i] != null) {
                    let _localeValue = JSON.parse(values[i]);

                    for(let _cLanguage in _localeValue) {
                        if(this._translationByLanguage[_cLanguage] != null) {
                            this._translationByLanguage[_cLanguage][i.split('@')[0]] = _localeValue[_cLanguage]
                        }
                    }

                    for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
                        // prendo la prima lingua che ha un valore e la uso come valore del campo di base
                        if(_localeValue[_cLanguage] != null && _localeValue[_cLanguage] != '') {
                            values[i.split('@')[0]] = _localeValue[_cLanguage];
                            break;
                        }
                    }
                }
                else {
                    values[i.split('@')[0]] = null;
                }
            }
        }

        for(let _cKey of _keysToRemove) {
            delete values[_cKey];
        }

        return values
    }

    isSaving = false;

    saveData() {
        if(!this.isSaving && this.formComponent.validateForm()) {
        
            this.isSaving = true;

            let _values = this.formLayout.getData();
            _values = this.adaptSaveData(_values);

            _values = this._updateTranslations(_values);
            this.beforeSaveData(_values).then((values) => {
                this.dataService.saveElementData(this.resourceId, this.elementId, values).subscribe((data) => {
                    if(data == null) {
                        this.isSaving = false;
                    }
                    else {
                        this._checkSaveTranslations(data).then(() => {
                            this.isSaving = false;
    
                            let _action:'update'|'insert' = 'update';
                            if(this.openMode == 'create') _action = 'insert'
    
                            this.notificationsService.addLocalNotification('forms-result-splash.action-text-' + _action, 'success', null, null, 3000)
    
                            this.router.navigate([this.returnUrl], { relativeTo: this.route })    
                        })
                    }
                })
            })
        }
    }

    private _checkSaveTranslations(data:any) {
        return new Promise<any>((resolve, reject) => {
            if(!this._hasTranslations) {
                resolve(data)
            }
            else {
                let _translationIdField = this._getTranslationsIdField();

                this.dataService.saveElementTranslations(data.id, this.resourceId + 'Translation', _translationIdField, data.languages, this._translationByLanguage).subscribe((data) => {
                    resolve(data)
                })
            }
        })
    }

    cancelEdit() {
        if(!this.isSaving) {
            this.router.navigate([this.returnUrl], { relativeTo: this.route, fragment: this.returnFragment })
        }
    }

    deleteDraft() {
        if(!this.isSaving) {
            this.isSaving = true;

            this.dataService.deleteDraftElement(this.resourceId, this.elementId).subscribe((data) => {
                this.isSaving = false;

                this.router.navigate([this.returnUrl], { relativeTo: this.route, fragment: this.returnFragment })
            })
        }
    }

    protected async remoteTranslateObjectArrayField(fields:Array<PgFormField>, languages:string, values:string) {
        let _selectedLanguages = PGUtilities.tryParseJSON(languages)

        let _objectArray = PGUtilities.tryParseJSON(values) || []
        
        let _requestFields:Array<string> = []
        for(let _field of fields) {
            if(_field.locale) {
                _requestFields.push(_field.name)
            }
        }

        let _requestList:Array<string> = []

        for(let _item of _objectArray) {                
            for(let _field of _requestFields) {
                _requestList.push(_item[_field])
            }
        }

        let _translatedFields = await new Promise<Array<string>>((resolve, reject) => {
            this.localizationService.remoteTranslateLocaleFields(_requestList, _selectedLanguages).subscribe((data) => {
                resolve(data)
            }, () => {
                reject()
            })
        })

        for(let _item of _objectArray) {                
            for(let _field of _requestFields) {
                _item[_field] = _translatedFields.shift()
            }
        }

        return JSON.stringify(_objectArray)
    }
}
